import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, BookOpen, ScrollText, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import { TextSizeControls } from '../components/TextSizeControls';
import { Switch } from '../components/ui/switch';
import { usePreferences } from '../context/PreferencesContext';
import { Label } from '../components/ui/label';

const WordAnalysisToggle = () => {
  const { preferences, setEnableWordAnalysis } = usePreferences();
  return (
    <div className="flex items-center justify-between gap-3" data-testid="word-analysis-toggle">
      <div className="flex-1">
        <Label htmlFor="enable-word-analysis" className="text-sm text-text-primary cursor-pointer">
          Mots cliquables dans le Coran
        </Label>
        <p className="text-xs text-text-muted mt-1">
          Affiche l'analyse lexicale (racine, lemme, type grammatical) au clic sur un mot.
        </p>
      </div>
      <Switch
        id="enable-word-analysis"
        checked={!!preferences.enableWordAnalysis}
        onCheckedChange={setEnableWordAnalysis}
        data-testid="enable-word-analysis-switch"
      />
    </div>
  );
};

const TajwidColoringToggle = () => {
  const { preferences, setEnableTajwidColoring } = usePreferences();
  return (
    <div className="flex items-center justify-between gap-3" data-testid="tajwid-coloring-toggle">
      <div className="flex-1">
        <Label htmlFor="enable-tajwid" className="text-sm text-text-primary cursor-pointer">
          Coloration Tajwīd dans le Coran
        </Label>
        <p className="text-xs text-text-muted mt-1">
          Met en évidence les règles de récitation (madd, ghunna, qalqala, ikhfā', idghām…)
          directement dans le texte arabe. Le clic sur un mot reste actif pour l'analyse lexicale.
        </p>
      </div>
      <Switch
        id="enable-tajwid"
        checked={!!preferences.enableTajwidColoring}
        onCheckedChange={setEnableTajwidColoring}
        data-testid="enable-tajwid-switch"
      />
    </div>
  );
};

const TajwidExerciseToggle = () => {
  const { preferences, setEnableTajwidExercise } = usePreferences();
  return (
    <div className="flex items-center justify-between gap-3" data-testid="tajwid-exercise-toggle-card">
      <div className="flex-1">
        <Label htmlFor="enable-tajwid-exercise" className="text-sm text-text-primary cursor-pointer">
          Mode exercice Tajwīd
        </Label>
        <p className="text-xs text-text-muted mt-1">
          Sur chaque sourate, l'app pré-liste les mots porteurs d'une règle.
          Tapotez un mot, choisissez la bonne règle parmi 4 propositions, et
          obtenez votre score par sourate.
        </p>
      </div>
      <Switch
        id="enable-tajwid-exercise"
        checked={!!preferences.enableTajwidExercise}
        onCheckedChange={setEnableTajwidExercise}
        data-testid="enable-tajwid-exercise-switch"
      />
    </div>
  );
};

const PreviewBlock = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" data-testid="display-settings-preview">
      <div className="flex items-center gap-2 mb-2">
        <Eye className="w-4 h-4 text-primary" />
        <span className="overline">{t('common.preview')}</span>
      </div>

      {/* Verse sample */}
      <div className="p-4 rounded-md bg-background/60 border border-border">
        <div className="flex items-center gap-2 text-xs text-primary mb-2">
          <BookOpen className="w-3.5 h-3.5" />
          {t('settings.text_quran_arabic')}
        </div>
        <p className="quran-arabic-text text-text-primary" dir="rtl">
          {t('settings.preview_arabic_sample')}
        </p>
        <div className="text-xs text-primary mt-3 mb-1">{t('settings.text_quran_translation')}</div>
        <p className="quran-translation-text text-text-secondary italic">
          « {t('settings.preview_translation_sample')} »
        </p>
      </div>

      {/* Hadith sample */}
      <div className="p-4 rounded-md bg-background/60 border border-border">
        <div className="flex items-center gap-2 text-xs text-primary mb-2">
          <ScrollText className="w-3.5 h-3.5" />
          {t('settings.text_hadith')}
        </div>
        <p className="hadith-translation-text text-text-secondary">
          {t('settings.preview_hadith_sample')}
        </p>
      </div>
    </div>
  );
};

export default function DisplaySettings() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();

  if (authLoading) return <PageLoading />;
  // Allow guests too — preferences are persisted locally
  // (logged-in users get them synced server-side automatically).

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="display-settings-page">
      <Link to="/settings">
        <Button variant="ghost" size="sm" className="text-text-muted hover:text-text-primary mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
      </Link>

      <SectionHeader
        overline={t('settings.title')}
        title={t('settings.section_display')}
        subtitle={t('settings.section_display_subtitle')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <GlassCard className="space-y-4" data-testid="settings-language-card">
            <LanguageSwitcher />
          </GlassCard>

          <GlassCard className="space-y-4" data-testid="settings-theme-card">
            <ThemeToggle />
          </GlassCard>

          <GlassCard className="space-y-4" data-testid="settings-text-card">
            <TextSizeControls />
          </GlassCard>

          <GlassCard className="space-y-3" data-testid="settings-word-analysis-card">
            <WordAnalysisToggle />
          </GlassCard>

          <GlassCard className="space-y-3" data-testid="settings-tajwid-card">
            <TajwidColoringToggle />
          </GlassCard>

          <GlassCard className="space-y-3" data-testid="settings-tajwid-exercise-card">
            <TajwidExerciseToggle />
          </GlassCard>

          {!user && (
            <p className="text-xs text-text-muted">
              {t('settings.saved_locally')}
            </p>
          )}
          {user && (
            <p className="text-xs text-text-muted">
              {t('settings.saved_synced')}
            </p>
          )}
        </div>

        <GlassCard className="lg:sticky lg:top-24 self-start">
          <PreviewBlock />
        </GlassCard>
      </div>
    </div>
  );
}
